import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Input, Button, Label } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { useKeyDown } from '../../../hooks/useKeyDown'

// Options:
// value: current value
// onChange: change callback
// min, max: minimum , maximum allowed values
// digits: number of digits after floating point
// buttonPlacement: button position [null, or "right" or "none"]
const NumericInput = ({ value, onChange, onChangeComplete, onUserInteraction, min, max, digits, buttonPlacement, disabled, selectOnFocus, prefix, nullable, ...rest }) => {
   const format = (val) => {
     if (val === "" || val === null || val === undefined) {
       return "";
     }
     const flt = parseFloat(val);
     if (_.isFinite(flt)) {
       return flt.toFixed(_.isUndefined(digits) ? 2 : digits);
     } else {
       return "";
     }
   };

   const [displayValue, setDisplayValue] = useState(() => {
     if (value === null || value === undefined) {
       return "";
     }
     return format(value);
   });

   const ref = useRef();

  useEffect(() => {
      if (ref.current?.inputRef.current !== document.activeElement) {
      //accept null and undefined as "reset" values while still filtering out real NaN.
      if (value === null || value === undefined || !isNaN(parseFloat(value))) {
        setDisplayValue(format(value))
      }
    }
  }, [value])

  useEffect(() => {
    if (onChange && displayValue != value) {
      if (
        displayValue === "" &&
        (value === null || typeof value === "undefined") &&
        nullable
      ) {
        return;
      } else if (displayValue === "" && nullable) {
        onChange(undefined);
      } else if (displayValue === "" && !nullable) {
        onChange(0); // Send 0 for non-nullable empty inputs
      } else {
        onChange(parseFloat(format(displayValue)));
      }
    }
  }, [displayValue]);

  const handleChange = (e, { value: val }) => {
    // Number format: allows thousands separators, decimals, +/- signs
    const numericRx = /^[+-]?(\d{1,3}(,\d{3})*|\d+)(\.\d*)?$/;

    // Negatives allowed if min is undefined or negative
    const allowNegative = !_.isNumber(min) || min < 0;
    const isBareMinus = val === "-" && allowNegative;
    const isBlank = val === "";
    const matchesNumber = numericRx.test(val);

    // Block invalid input
    if (!isBlank && !isBareMinus && !matchesNumber) return;

    // Empty nullable → null
    if (isBlank && nullable) {
      setDisplayValue("");
      onChange?.(undefined);
      return;
    }

    // Validate bounds
    const flt = parseFloat(val);
    if (_.isFinite(flt)) {
      if (_.isNumber(min) && flt < min) return;
      if (_.isNumber(max) && flt > max) return;
    }

    // Update display
    setDisplayValue(val);

    // Notify parent
    if (isBlank) {
      // Empty non-nullable: use 0
      onChange?.(0);
    } else if (isBareMinus) {
      // Don't send NaN while typing negative
      return;
    } else {
      // Valid number
      onChange?.(parseFloat(format(flt)));
    }
  };



  const onBlur = () => {
    setDisplayValue((prev) => format(prev));
    if (displayValue === "" && nullable) {
      onChangeComplete?.(undefined);
    } else if (displayValue === "" && !nullable) {
      onChangeComplete?.(0);
    } else {
      onChangeComplete?.(parseFloat(format(displayValue)));
    }
  };

  const isNumber = (value) => {
    return typeof value === 'number' && isFinite(value) && !isNaN(value);
    }

  const clamp = (v, min, max) => {
    if (isNumber(min) && v < min) return min;
    if (isNumber(max) && v > max) return max;
    return v;
    };

  const adjust = (delta) => {
    setDisplayValue((prev) => {
      let base = isNumber(parseFloat(prev))
        ? parseFloat(prev)
        : isNumber(min)
        ? min
        : 0;

      let next = clamp(base + delta, min, max);

      onChangeComplete?.(next);
      return format(next);
    });
  };

  const increase = () => { onUserInteraction?.(); adjust(+1); };
  const decrease = () => { onUserInteraction?.(); adjust(-1); };

  const checkIfFocused = () =>
    ref.current?.inputRef.current === document.activeElement;

  const keyHandlers = useMemo(
    () => ({
      ArrowUp: () => {
        if (checkIfFocused()) {
          increase();
        }
      },
      ArrowDown: () => {
        if (checkIfFocused()) {
          decrease();
        }
      },
      Enter: (event) => {
        if (checkIfFocused() && event.target.nodeName !== "TEXTAREA") {
          event.preventDefault();
        }
      },
    }),
    [increase, decrease]
  );

  useKeyDown(keyHandlers);



  const selectTextOnFocus = (e) => selectOnFocus ? e.target.select() : undefined

  const decreaseStyle = buttonPlacement === "right" ? { flex: 0.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 0, padding: "0em 0.2em" } : {}
  const increaseStyle = buttonPlacement === "right" ? { flex: 0.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, padding: "0em 0.2em" } : { borderTopRightRadius: "0.285714rem", borderBottomRightRadius: "0.285714rem" }
  
  // Prevent mousedown from causing blur/focus change which exits grid edit mode
  const preventBlur = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const decreaseButton = <Button icon={buttonPlacement === "right" ? "triangle down" : "minus"} size="small" disabled={value <= min || disabled} style={decreaseStyle} onClick={decrease} onMouseDown={preventBlur} />
  const increaseButton = <Button icon={buttonPlacement === "right" ? "triangle up" : "plus"} size="small" disabled={value >= max || disabled} style={increaseStyle} onClick={increase} onMouseDown={preventBlur} />
  switch (buttonPlacement) {
    case "none":
      return <Input ref={ref} value={displayValue} onChange={handleChange} onBlur={onBlur} min={min} max={max} disabled={disabled} {...rest}>
        <input onFocus={selectTextOnFocus} />
      </Input>
    case "right":
      return <Input actionPosition={buttonPlacement === "right" ? null : "left"} labelPosition="right" ref={ref} value={displayValue} onChange={handleChange} onBlur={onBlur} disabled={disabled} min={min} max={max} {...rest}>
        {prefix && <Label>{prefix}</Label>}
        <input onFocus={selectTextOnFocus} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          {increaseButton}
          {decreaseButton}
        </div>
      </Input>
    default:
      return (
        <Input
          actionPosition={buttonPlacement === 'right' ? null : 'left'}
          labelPosition='right'
          ref={ref}
          value={displayValue}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          min={min}
          max={max}
          {...rest}
        >
          {decreaseButton}
          <input onFocus={selectTextOnFocus} />
          {increaseButton}
        </Input>
      );
  }
}

NumericInput.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  onUserInteraction: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  digits: PropTypes.number,
  buttonPlacement: PropTypes.oneOf(["right", "leftAndRight", "none"]),
  disabled: PropTypes.bool,
  onChangeComplete: PropTypes.func,
  selectOnFocus: PropTypes.bool,
  nullable: PropTypes.bool,
  prefix: PropTypes.string
}
export default NumericInput
